import { useRouter } from 'next/router';
import { toast } from 'react-hot-toast';

import { api } from '@/utils/api';

export const useVerifyContactEmailMutation = () => {
  const utils = api.useUtils();
  return api.contact.verifyEmail.useMutation({
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });
    },
  });
};

export const useRemoveContactFieldValueMutation = () => {
  const utils = api.useUtils();
  return api.contact.deleteValue.useMutation({
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });
    },
  });
};

export const useUpdateContactMutation = () => {
  const utils = api.useUtils();
  return api.contact.update.useMutation({
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });
    },
  });
};

export const useRemoveContactFieldPrimaryMutation = () => {
  const utils = api.useUtils();
  return api.contact.unsetPrimaryField.useMutation({
    onSuccess: () => {
      void utils.contact.byId.invalidate();
    },
  });
};

export const useMakeContactFieldPrimaryMutation = () => {
  const utils = api.useUtils();
  return api.contact.setPrimaryField.useMutation({
    onSuccess: () => {
      void utils.contact.byId.invalidate();
    },
  });
};

export const useRegenerateDossierMutation = () => {
  const utils = api.useUtils();
  return api.contact.regenerateDossierCtx.useMutation({
    // The mutation now resolves with a discriminated result instead of throwing
    // on a logical failure, so only refetch when generation actually succeeded.
    // A failed attempt is surfaced by the caller as a persistent error card.
    onSuccess: (data) => {
      if (data.ok) void utils.contactDetailBootstrap.forContact.invalidate();
    },
  });
};

export const useEnhanceContactMutation = () => {
  const utils = api.useUtils();
  return api.contact.enhance.useMutation({
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });
      void utils.team.id.invalidate();
      void utils.cadence.cadences2.invalidate();
      void utils.cadence.cadencesForCards.invalidate();
      void utils.contact.search.invalidate();
    },
  });
};

export const useDeleteDeadContactsMutation = () => {
  const utils = api.useUtils();
  return api.contact.deleteDeadContacts.useMutation({
    onSuccess: () => {
      // The contacts are gone — drop them from Search and any list/count that showed them.
      void utils.contact.search.invalidate();
      void utils.contact.byId.invalidate();
      void utils.team.id.invalidate();
      void utils.cadence.cadences2.invalidate();
      void utils.cadence.cadencesForCards.invalidate();
    },
  });
};

export const useContactSetStageMutation = () => {
  const utils = api.useUtils();
  return api.contact.setStage.useMutation({
    onSuccess: () => {
      void utils.contact.byId.invalidate();
      void utils.contact.byOrg2.invalidate();
      void utils.superAdmin.contact.invalidate();
      void utils.sharing.paginated.invalidate();
      void utils.stage.all.invalidate();
      void utils.stage.withOrgs.invalidate();
      void utils.stage.withContacts.invalidate();
      void utils.contact.getConnectedGroupedByOrg.invalidate();
      void utils.contact.getConnected.invalidate();
      void utils.history.contact.invalidate();
      void utils.superAdmin.doneForYouUserResponses.invalidate();
      void utils.superAdmin.doneForYouUserWaitingForReply.invalidate();
    },
  });
};

export const useContactBulkSetStageMutation = () => {
  const utils = api.useUtils();
  return api.contact.bulkSetStage.useMutation({
    onSuccess: () => {
      void utils.contact.byId.invalidate();
      void utils.contact.byOrg2.invalidate();
      void utils.contact.getConnected.invalidate();
      void utils.contact.getConnectedGroupedByOrg.invalidate();
      void utils.stage.withContacts.invalidate();
      void utils.stage.withOrgs.invalidate();
    },
  });
};

export const useContactAckContentViewAndResponse = () => {
  const utils = api.useUtils();
  return api.contact.ackContentViewAndResponse.useMutation({
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });
      void utils.contact.contentViewed.invalidate();
      void utils.contact.responded.invalidate();
      void utils.globalReminder.paginated.invalidate();
    },
  });
};

export const useContactBulkAckInteraction = () => {
  const utils = api.useUtils();
  return api.contact.bulkAckInteraction.useMutation({
    onSuccess: () => {
      void utils.contact.byId.invalidate();
      void utils.contact.contentViewed.invalidate();
      void utils.contact.responded.invalidate();
      void utils.globalReminder.paginated.invalidate();
    },
  });
};

export const useEmailInteractionsSummary = () => {
  return api.contact.emailInteractionsSummary.useMutation();
};

export const useContactSetTrackingMutation = () => {
  const utils = api.useUtils();
  return api.contact.setTracked.useMutation({
    onMutate: ({ contactId, data: { tracked } }) => {
      utils.contact.byId.setData({ contactId }, (data) =>
        Object.assign(
          {},
          data,
          data?.contactV2 ? { contactV2: Object.assign({}, data.contactV2, { tracked }) } : undefined,
        ),
      );
    },
    onError: () => {
      void utils.contact.invalidate();
    },
  });
};

export const useContactSetDisqualifiedMutation = () => {
  const utils = api.useUtils();
  return api.contact.setDisqualified.useMutation({
    onMutate: ({ contactId, disqualified }) => {
      utils.contact.byId.setData({ contactId }, (data) =>
        Object.assign(
          {},
          data,
          data?.authedUser ? { authedUser: Object.assign({}, data.authedUser, { disqualified }) } : undefined,
        ),
      );
    },
    onSuccess: () => {
      void utils.contact.invalidate();
    },
    onError: () => {
      void utils.contact.invalidate();
    },
  });
};

export const useMarkInteractionAsMeetingBookedMutation = () => {
  const utils = api.useUtils();
  return api.contact.markInteractionAsMeetingBooked.useMutation({
    onSuccess: () => {
      void utils.contact.byId.invalidate();
      void utils.contact.invalidate();
      void utils.history.contact.invalidate();
    },
  });
};

export const useDeleteMeetingBookedMutation = () => {
  const utils = api.useUtils();
  return api.contact.deleteMeetingBooked.useMutation({
    onSuccess: (data, variables) => {
      void utils.contact.getMeetingBookedDetails.invalidate({ contactId: variables.contactId });
      void utils.contact.byId.invalidate({ contactId: variables.contactId });
      void utils.history.contact.invalidate();
      if (data.warning) {
        toast(data.warning, { icon: '⚠️' });
      }
    },
  });
};

export const useContactSetMinAgeOverrideMutation = () => {
  const utils = api.useUtils();
  return api.contact.setMinAgeOverride.useMutation({
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });
    },
  });
};

export const useContactResetGenderMutation = () => {
  const utils = api.useUtils();
  return api.contact.resetGender.useMutation({
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });
    },
  });
};

export const useResearchFromLinkedInMutation = () => {
  const utils = api.useUtils();
  const router = useRouter();
  return api.contact.researchFromLinkedIn.useMutation({
    onSuccess: (data, variables) => {
      void utils.contact.byId.invalidate();
      void utils.contact.search.invalidate();
      void utils.contact.getConnected.invalidate();
      void utils.contact.getConnectedGroupedByOrg.invalidate();
      void utils.contactDetailBootstrap.forContact.invalidate();
      void utils.cadence.cadences2.invalidate();

      const mergedCount = data.mergedFromContactPublicIds?.length ?? 0;
      if (mergedCount === 0) return;

      const total = mergedCount + 1;
      toast.success(`Merged ${total} duplicate contacts into one`);

      // The contact in the URL was merged away — redirect to the surviving winner
      // so the user doesn't end up on a now-hidden (`merged_into IS NOT NULL`) page.
      const urlContactId =
        typeof router.query.contactId === 'string' ? router.query.contactId : undefined;
      if (urlContactId === variables.contactId && data.winnerContactPublicId !== variables.contactId) {
        void router.replace({
          pathname: router.pathname,
          query: { ...router.query, contactId: data.winnerContactPublicId },
        });
      }
    },
  });
};

export const useDeleteForeignMappingMutation = () => {
  const utils = api.useUtils();
  return api.contact.deleteForeignMapping.useMutation({
    onSuccess: (_, variables) => {
      void utils.contact.getForeignMappingsAll.invalidate({ contactId: variables.contactId });
      void utils.contact.byId.invalidate({ contactId: variables.contactId });
    },
  });
};

export const useAddForeignMappingMutation = () => {
  const utils = api.useUtils();
  return api.contact.addForeignMapping.useMutation({
    onSuccess: (data, variables) => {
      void utils.contact.getForeignMappingsAll.invalidate({ contactId: variables.contactId });
      void utils.contact.byId.invalidate({ contactId: variables.contactId });
      // If contact was reactivated, also invalidate cadences
      if (data.reactivated) {
        void utils.cadence.cadences2.invalidate();
        void utils.cadence.fetchPaginatedExecutables.invalidate();
      }
    },
  });
};

export const useVerifyForeignMappingMutation = () => {
  const utils = api.useUtils();
  return api.contact.verifyForeignMapping.useMutation({
    onSuccess: (_, variables) => {
      void utils.contact.getForeignMappingsAll.invalidate({ contactId: variables.contactId });
      void utils.contact.byId.invalidate({ contactId: variables.contactId });
    },
  });
};